import React from 'react';

import KeyStatTileSkeleton from '../../KeyStatTileSkeleton';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import 'react-loading-skeleton/dist/skeleton.css';

const EM_DASH = '—';

// Mirrors KpiTiles.js (IB-640): a free-text context line + sign-based colour,
// so it intentionally does not reuse CommonComponents/KeyStatTile (which models
// a ±% variance with a triangle). KeyStatTileSkeleton is still reused for the
// loading state.
function CashflowKpiTile({ title, value, valueClassName, context }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{title}</div>
            <div className={`tw-text-2.5xl tw-font-semibold ${valueClassName || 'tw-text-charcoal'}`}>{value}</div>
            {context && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">{context}</div>}
        </div>
    );
}

// Headline cashflow analytics for the cashflow tab (IB-642 Phase 2): lifetime
// net, the signed peak funding gap, the break-even month (or a "Beyond date
// range" hint when never reached), and total gross inflow. Em-dash tiles when
// no projects are selected, matching the KPI tiles above the tabs.
export default function CashflowKpis({ loading, cashflowKpis, monthlyNet, selectedCount, onExtendRange }) {
    if (loading) {
        return (
            <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3 tw-mb-4">
                {[0, 1, 2, 3].map((i) => (
                    <KeyStatTileSkeleton key={i} />
                ))}
            </div>
        );
    }

    const hasData = selectedCount > 0 && !!cashflowKpis;

    const lifetimeNet = hasData ? cashflowKpis.lifetime_net : 0;
    const peakGap = hasData ? cashflowKpis.peak_funding_gap : 0;

    const lifetimeNetValue = hasData ? formatToFixedMoneyWithLocale(lifetimeNet) : EM_DASH;
    const peakGapValue = hasData ? formatToFixedMoneyWithLocale(peakGap) : EM_DASH;
    const inflowValue = hasData ? formatToFixedMoneyWithLocale(cashflowKpis.cumulative_inflow) : EM_DASH;

    const lifetimeNetColour = hasData ? (lifetimeNet >= 0 ? 'tw-text-green' : 'tw-text-red') : 'tw-text-charcoal';
    // A funding gap is only a concern when negative; the all-positive case is 0.
    const peakGapColour = hasData && peakGap < 0 ? 'tw-text-red' : 'tw-text-charcoal';

    const breakEvenLabel = hasData ? breakEvenMonthLabel(cashflowKpis.break_even, monthlyNet) : EM_DASH;
    const breakEvenReached = hasData && cashflowKpis.break_even !== null;

    return (
        <div className="tw-grid tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3 tw-mb-4">
            <CashflowKpiTile title={$t('Lifetime net')} value={lifetimeNetValue} valueClassName={lifetimeNetColour} />
            <CashflowKpiTile title={$t('Peak funding gap')} value={peakGapValue} valueClassName={peakGapColour} />
            <CashflowKpiTile
                title={$t('Break-even')}
                value={breakEvenLabel}
                context={
                    hasData && !breakEvenReached && onExtendRange ? (
                        <button type="button" className="tw-text-blue tw-underline" onClick={onExtendRange}>
                            {$t('Extend the date range')}
                        </button>
                    ) : null
                }
            />
            <CashflowKpiTile title={$t('Cumulative inflow')} value={inflowValue} />
        </div>
    );
}

// Resolve the human-readable label for the break-even 'm-Y' key from the monthly
// series. A null key means cumulative never recovered within the range.
function breakEvenMonthLabel(breakEvenKey, monthlyNet) {
    if (!breakEvenKey) {
        return $t('Beyond date range');
    }

    const match = (monthlyNet || []).find((entry) => entry.key === breakEvenKey);

    return match ? match.label : breakEvenKey;
}
